export const INTERFACE_LABELS = {
  eth1: 'Ethernet',
  eth0: 'Ethernet',
  wlan0: 'Wi-Fi',
  wwan0: 'LTE',
};

export const APP_SERVICE_LABELS = {
  remoteMonitor: 'Remote Monitor',
  ai_engine: 'AI Engine',
  iomt_backend: 'IOMT Backend',
  psql: 'PostgreSQL',
  fota: 'FOTA Updater',
  connectivity: 'Connectivity Manager',
  backend: 'Backend',
  ui: 'UI',
  router: 'Router',
};

export const CRITICAL_APPS = new Set([
  'remoteMonitor',
  'ai_engine',
  'iomt_backend',
  'psql',
  'connectivity',
]);

export const CRITICAL_SOM_APPS = new Set([
  'backend',
  'ui',
  'fota',
  'router',
]);

export const BOARD = {
  IOMT: 'iomt',
  SOM: 'som',
};

/** Map wire severities (incl. EARLY_WARN) to UI tone classes. */
export const normalizeAlertSeverity = (severity) => {
  const value = String(severity || 'WARN').toUpperCase();
  if (value === 'CRITICAL') return 'critical';
  if (value === 'EARLY_WARN') return 'early_warn';
  if (value === 'WARN') return 'warn';
  return 'warn';
};

export const hasSomSnapshot = (health) => Boolean(health?.som);

export const getBoardHealthScore = (health, board) => {
  if (board === BOARD.SOM) return health?.som?.healthScore ?? null;
  return health?.healthScore ?? null;
};

export const formatMachineSerial = (machineUDI, activeDevice) => {
  if (!machineUDI) return '—';
  if (activeDevice === 'NX5') {
    return machineUDI.split('-').at(-1);
  }
  return machineUDI.split('-').filter((part) => part.includes('1G')).pop() || machineUDI.split('-').at(-1);
};

export const formatUptime = (uptimeSec) => {
  if (uptimeSec == null || Number.isNaN(Number(uptimeSec))) return '—';
  const totalHours = Number(uptimeSec) / 3600;
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const remHours = totalHours % 24;
    return `${days}d ${remHours.toFixed(1)} Hours`;
  }
  if (totalHours >= 1) return `${totalHours.toFixed(1)} Hours`;
  const totalMinutes = Math.floor(Number(uptimeSec) / 60);
  return `${totalMinutes} Minutes`;
};

/** Compact process-age label: 2d 5h / 14h 33m / 12m */
export const formatProcessUptime = (uptimeSec) => {
  if (uptimeSec == null || Number.isNaN(Number(uptimeSec))) return '—';
  const s = Math.max(0, Math.floor(Number(uptimeSec)));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const formatUptimeDisplay = (uptimeHours, uptimeSec) => {
  if (uptimeHours != null && !Number.isNaN(Number(uptimeHours))) {
    const hours = Number(uptimeHours);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `${days}d ${remHours.toFixed(1)} Hours`;
    }
    if (hours >= 1) return `${hours.toFixed(1)} Hours`;
    return `${Math.round(hours * 60)} Minutes`;
  }
  return formatUptime(uptimeSec);
};

export const seriesToSparkValues = (series, fallback = null) => {
  if (!Array.isArray(series) || series.length === 0) {
    return fallback;
  }
  const values = series
    .map((point) => point?.v)
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .map(Number);
  if (values.length === 0) return fallback;
  return values;
};

export const sparkValuesWithFallback = (series, snapshotValue) => {
  const fromSeries = seriesToSparkValues(series);
  if (fromSeries?.length) return fromSeries;
  if (snapshotValue != null && Number.isFinite(Number(snapshotValue))) {
    const value = Number(snapshotValue);
    return [value, value, value];
  }
  return null;
};

export const seriesWithFallback = (series, snapshotValue) => {
  if (Array.isArray(series) && series.length > 0) {
    const filtered = series.filter(
      (point) => point?.v != null && Number.isFinite(Number(point.v))
    );
    if (filtered.length > 0) {
      return filtered.map((point) => ({ t: point.t ?? null, v: Number(point.v) }));
    }
  }
  if (snapshotValue != null && Number.isFinite(Number(snapshotValue))) {
    const value = Number(snapshotValue);
    return [{ t: null, v: value }, { t: null, v: value }, { t: null, v: value }];
  }
  return [];
};

export const hasHealthSnapshot = (health) => {
  if (!health) return false;
  if (health.healthScore != null) return true;
  const system = health.system || {};
  return [
    system.load1m,
    system.memTotalMb,
    system.rootUsedPct,
    health.uptimeSec,
    health.uptimeHours,
  ].some((value) => value != null);
};

export const getHealthScoreMeta = (score) => {
  switch ((score || '').toLowerCase()) {
    case 'ok':
      return { label: 'Healthy', className: 'mh-health--ok' };
    case 'degraded':
      return { label: 'Degraded', className: 'mh-health--degraded' };
    case 'critical':
      return { label: 'Critical', className: 'mh-health--critical' };
    default:
      return { label: 'Unknown', className: 'mh-health--unknown' };
  }
};

export const getNetworkIp = (health, iface, version = 4) => {
  const data = health?.network?.ipAddress?.[iface];
  if (!data) return null;
  return formatIpValue(version === 4 ? (data.ipV4 ?? data.ipv4) : (data.ipV6 ?? data.ipv6));
};

export const getInterfaceStatus = (metrics = {}) => {
  if (metrics.validated === false) {
    return { label: 'Not validated', className: 'mh-status--muted' };
  }
  if (metrics.validated === true && Number(metrics.packetLossPct) === 0) {
    return { label: 'OK', className: 'mh-status--ok' };
  }
  if (metrics.validated === true && Number(metrics.packetLossPct) > 0) {
    return { label: 'Degraded', className: 'mh-status--warn' };
  }
  return { label: 'Unknown', className: 'mh-status--muted' };
};

const formatIpValue = (value) => {
  if (!value || value === 'N/A' || value === 'null' || value === 'undefined') {
    return null;
  }
  return value;
};

const pickNumber = (...values) => {
  for (const value of values) {
    if (value == null || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const pickValue = (...values) => {
  for (const value of values) {
    if (value != null && value !== '') return value;
  }
  return null;
};

/** Snapshot + wire apps may be an array or a name-keyed object, camelCase or snake_case. */
export const normalizeApp = (name, app = {}) => {
  const rssFromKb = app.rss_kb != null ? Number(app.rss_kb) / 1024 : null;
  const swapFromKb = app.swap_kb != null ? Number(app.swap_kb) / 1024 : null;
  const uptimeSec = pickNumber(app.uptimeSec, app.activeSec, app.active_sec, app.uptime_sec);
  const activeSince = pickValue(app.activeSince, app.active_since);

  return {
    name,
    state: app.state ?? null,
    pid: app.pid ?? null,
    rssMb: pickNumber(app.rssMb, app.rss_mb, rssFromKb),
    swapMb: pickNumber(app.swapMb, swapFromKb),
    cpuPct: pickNumber(app.cpuPct, app.cpu_pct),
    uptimeSec,
    restarts: pickNumber(app.restarts),
    activeSince,
    systemd: app.systemd ?? null,
    comm: app.comm ?? null,
  };
};

export const normalizeAppsList = (apps) => {
  if (Array.isArray(apps)) {
    return apps
      .filter((app) => app && (app.name || app.app))
      .map((app) => normalizeApp(app.name || app.app, app));
  }
  if (apps && typeof apps === 'object') {
    return Object.entries(apps).map(([name, app]) => normalizeApp(name, app || {}));
  }
  return [];
};

export const mergeAppRuntime = (formattedApps, rawApps) => {
  const formatted = normalizeAppsList(formattedApps);
  const raw = normalizeAppsList(rawApps);
  if (!formatted.length) return raw;

  const rawByName = Object.fromEntries(raw.map((app) => [app.name, app]));
  return formatted.map((app) => {
    const extra = rawByName[app.name] || {};
    return {
      ...app,
      uptimeSec: app.uptimeSec ?? extra.uptimeSec,
      restarts: app.restarts ?? extra.restarts,
      activeSince: app.activeSince ?? extra.activeSince,
    };
  });
};

export const parseMachineRecord = (record) => {
  if (!record) return null;

  const raw = record.ipAddress;
  let payload = null;

  if (typeof raw === 'string') {
    payload = {
      legacy: true,
      display: raw,
      interfaces: {},
      interfaceMetrics: {},
      internetSpeed: null,
      activeInterface: null,
      systemHealth: record.systemHealth || null,
    };
  } else if (raw?.ipAddress && typeof raw.ipAddress === 'object') {
    payload = {
      legacy: false,
      interfaces: raw.ipAddress,
      interfaceMetrics: raw.interfaceMetrics || {},
      internetSpeed: raw.internetSpeed || null,
      activeInterface: raw.activeInterface || null,
      systemHealth: raw.systemHealth || record.systemHealth || null,
    };
  } else if (raw && (raw.wlan0 || raw.wwan0 || raw.eth0 || raw.eth1)) {
    payload = {
      legacy: false,
      interfaces: raw,
      interfaceMetrics: record.interfaceMetrics || {},
      internetSpeed: record.internetSpeed || null,
      activeInterface: record.activeInterface || null,
      systemHealth: record.systemHealth || null,
    };
  }

  if (!payload) return null;

  return {
    machineUDI: record.machineUDI,
    updatedAt: record.updatedAt,
    ...payload,
  };
};

export const extractRawAppsFromIpRecord = (record) => {
  if (!record) return { iomt: null, som: null };
  const parsed = parseMachineRecord(record);
  const health = parsed?.systemHealth || record.systemHealth || null;
  return {
    iomt: health?.apps || record.systemHealth?.apps || null,
    som: record.somHealth?.apps || health?.som?.apps || health?.somHealth?.apps || null,
  };
};

export const appsNeedRawRuntime = (health) => {
  const lists = [health?.apps, health?.som?.apps];
  return lists.some((apps) => {
    const normalized = normalizeAppsList(apps);
    return normalized.some((app) => app.uptimeSec == null || app.activeSince == null);
  });
};

export const extractRawHeartbeatFromIpRecord = (record) => {
  if (!record) return { iomt: null, som: null };
  const parsed = parseMachineRecord(record);
  const iomt = parsed?.systemHealth || record.systemHealth || null;
  return {
    iomt,
    som: record.somHealth || iomt?.som || iomt?.somHealth || null,
  };
};

export const mergeHeartbeatBoard = (snapshot, rawHealth) => {
  const system = snapshot?.system || {};
  const rawSystem = rawHealth?.system || {};
  const memTotalKb = pickNumber(system.memTotalKb, system.mem_total_kb, rawSystem.mem_total_kb);
  const memAvailableKb = pickNumber(system.memAvailableKb, system.mem_available_kb, rawSystem.mem_available_kb);
  const memFreeKb = pickNumber(system.memFreeKb, system.mem_free_kb, rawSystem.mem_free_kb);
  const swapUsedKb = pickNumber(system.swapUsedKb, system.swap_used_kb, rawSystem.swap_used_kb);
  const swapTotalKb = pickNumber(system.swapTotalKb, system.swap_total_kb, rawSystem.swap_total_kb);

  return {
    appVersion: pickValue(
      snapshot?.appVersion,
      snapshot?.app_version,
      rawHealth?.app_version,
      rawHealth?.appVersion
    ),
    iomtKernelVersion: pickValue(
      snapshot?.iomtKernelVersion,
      snapshot?.iomt_kernel_version,
      rawHealth?.iomt_kernel_version,
      rawHealth?.iomtKernelVersion
    ),
    buildId: pickValue(
      snapshot?.buildId,
      snapshot?.build_id,
      rawHealth?.build_id,
      rawHealth?.buildId
    ),
    system: {
      ...system,
      memTotalKb,
      memAvailableKb,
      memFreeKb,
      swapUsedKb,
      swapTotalKb,
      swapTotalMb: pickNumber(
        system.swapTotalMb,
        system.swap_total_mb,
        rawSystem.swap_total_mb,
        swapTotalKb != null ? swapTotalKb / 1024 : null
      ),
      pgmajfault: pickNumber(system.pgmajfault, rawSystem.pgmajfault),
    },
  };
};

export const getInterfaceIp = (parsed, iface, version) => {
  const data = parsed?.interfaces?.[iface];
  if (!data) return null;
  return formatIpValue(version === 4 ? (data.ipV4 ?? data.ipv4) : (data.ipV6 ?? data.ipv6));
};

export const getMemoryUsedPct = (system) => {
  if (!system?.mem_total_mb) return 0;
  const used = system.mem_total_mb - (system.mem_available_mb || 0);
  return Math.min(100, Math.max(0, Math.round((used / system.mem_total_mb) * 100)));
};

export const getDiskStatus = (pct) => {
  if (pct == null) return { label: 'N/A', className: 'mh-disk--muted' };
  if (pct >= 80) return { label: 'Danger', className: 'mh-disk--danger' };
  if (pct >= 70) return { label: 'Warning', className: 'mh-disk--warn' };
  return { label: 'Good', className: 'mh-disk--good' };
};

export const kbToMb = (kb) => {
  if (kb == null) return null;
  return (Number(kb) / 1024).toFixed(1);
};
